import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { Building2, ChevronRight, ExternalLink, Info, Layers, Loader2, Maximize2, Minimize2, Share2 } from 'lucide-react';
import { loadStaticData } from '@/lib/offlineData';
import { isNativeApp } from '@/lib/platform';
import { hapticLight, hapticMedium } from '@/lib/haptics';
import { getHospitalGradeInfo } from '@/lib/grades';
import { useIsMobile } from '@/hooks/use-mobile';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ShareDignityModal } from './ShareDignityModal';

interface ProcedureDetail {
  procedure_code: string;
  procedure_description: string;
  /** AI consumer-friendly name from codes_stats_<type>.json (llm_quick_name) — NOT the official code descriptor */
  consumer_name?: string;
  hospital_gross_charge: number;
  national_avg_gross: number;
  national_median_gross?: number;
  national_p10_gross?: number;
  national_p90_gross?: number;
  gross_markup_percentage: number;
  duplicate_count: number;
  inpatient_outpatient: string;
  normalized_unit: string;
  ndc_code: string;
  total_hospitals?: number;
  hospital_min_negotiated?: number;
  hospital_max_negotiated?: number;
}

/** Hospital-level quality + grade inputs, lifted from the rankings row the
 *  page already holds (rankings.json — static, so it works in the native app). */
export interface HospitalQualitySnapshot {
  /** Overall median-baseline markup — what the A–F grade and overall rank are computed from */
  overallMedianMarkup?: number | null;
  overallRank?: number | null;
  cmsStars?: number | null;
  cmsMortality?: number | null;
  cmsSafety?: number | null;
  cmsReadmission?: number | null;
  /** HCAHPS "would definitely recommend" — the hospital's own past patients, CMS survey. Never ours. */
  recommendPct?: number | null;
  googleRating?: number | null;
  googleReviews?: number | null;
}

interface ProcedureDrilldownModalProps {
  isOpen: boolean;
  onClose: () => void;
  hospitalId: string | null;
  hospitalName: string;
  procedureType: string | null;
  avgMarkup: number;
  /** Median markup for the same hospital × code type — gets equal billing with avg (it is the site's headline metric) */
  medianMarkup?: number | null;
  quality?: HospitalQualitySnapshot | null;
}

// Map internal codeType → URL slug used by ProcedureCodePage routes
const CODE_TYPE_TO_URL: Record<string, string> = {
  cpt: 'cpt',
  hcpcs: 'hcpcs',
  drg: 'drg',
  ndc: 'ndc',
  revenue: 'rev',
  supply: 'supply',
  drug: 'drugs',
};

const codePagePath = (codeType: string | null, code: string): string => {
  if (!codeType) return '';
  const slug = CODE_TYPE_TO_URL[codeType] || codeType;
  return `/${slug}/${encodeURIComponent(code)}`;
};

// ── National stats lookup (codes_stats_<type>.json) ─────────────────────────
// One static file per code type, keyed by code, emitted by
// scripts/generate_static_cache.py (code_stats) and served from R2. Replaces
// what used to be up to ~13 Supabase .in() chunks per open — this class of
// lookup never hits the DB from the client — and works in the native app,
// where Supabase is unreachable by design. Keys use the same code form as the
// per-hospital shards (bare DRG '795', dashed NDC).
interface CodeStat { med?: number; p10?: number; p90?: number; avg?: number; th?: number; n?: string }
type CodeStatsMap = Record<string, CodeStat>;
// Only types that have a file; anything else short-circuits to null, no 404 round-trip.
const STATS_FILE_SLUG: Record<string, string> = { cpt: 'cpt', hcpcs: 'hcpcs', drg: 'drg', revenue: 'rev', ndc: 'ndc' };
const codeStatsCache = new Map<string, Promise<CodeStatsMap | null>>();

function loadCodeStats(ptype: string): Promise<CodeStatsMap | null> {
  const slug = STATS_FILE_SLUG[ptype];
  if (!slug) return Promise.resolve(null);
  let p = codeStatsCache.get(slug);
  if (!p) {
    // cdnOnly: these files are kept out of the Pages bundle on purpose (the
    // pipeline refreshes them on R2 without a redeploy), so go straight to the
    // CDN — exactly one request per code type per session.
    p = loadStaticData<{ data: CodeStatsMap }>(`codes_stats_${slug}.json`, { cdnOnly: true })
      .then(w => (w && w.data ? w.data : null))
      .catch(() => null)
      .then(v => {
        // Cache hits for the session; a miss (404 / offline) is not cached so
        // the next open retries instead of pinning dashes until reload.
        if (v == null) codeStatsCache.delete(slug);
        return v;
      });
    codeStatsCache.set(slug, p);
  }
  return p;
}

/** Join national median / 10–90% band / hospital count / consumer name onto
 *  rows from the static lookup. Best-effort and silent: no file → rows come
 *  back untouched (dashes). Callers must NOT block first paint on this. */
async function enrichNationalStats(rows: ProcedureDetail[], ptype: string): Promise<ProcedureDetail[]> {
  const stats = await loadCodeStats(ptype);
  if (!stats) return rows;
  return rows.map(r => {
    const st = stats[r.procedure_code];
    if (!st) return r;
    return {
      ...r,
      national_median_gross: st.med ?? r.national_median_gross,
      national_p10_gross: st.p10 ?? r.national_p10_gross,
      national_p90_gross: st.p90 ?? r.national_p90_gross,
      total_hospitals: r.total_hospitals ?? st.th,
      consumer_name: st.n || r.consumer_name,
    };
  });
}

export const ProcedureDrilldownModal = ({
  isOpen,
  onClose,
  hospitalId,
  hospitalName,
  procedureType,
  avgMarkup,
  medianMarkup = null,
  quality = null,
}: ProcedureDrilldownModalProps) => {
  const { t } = useTranslation(['homepage', 'common']);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [procedures, setProcedures] = useState<ProcedureDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  // The exact row behind selectedCode — a code can appear on several rows
  // (settings/units); the embed pins THIS row's listed price on its price band.
  const [selectedProc, setSelectedProc] = useState<ProcedureDetail | null>(null);
  const [iframeAnchor, setIframeAnchor] = useState<string>('');  // e.g. '#variations'
  const [iframeFullscreen, setIframeFullscreen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // Build a shareable URL that re-opens this modal pre-loaded with the same hospital + code type
  const shareUrl = useMemo(() => {
    if (!hospitalId || !procedureType) return '';
    const u = new URL('/hospital-rankings', window.location.origin);
    u.searchParams.set('breakdown', hospitalId);
    u.searchParams.set('type', procedureType);
    if (selectedCode) u.searchParams.set('code', selectedCode);
    return u.toString();
  }, [hospitalId, procedureType, selectedCode]);
  // Global bucket counts per code (from codes_buckets_<type>.json — same data source as /cpt/<code>/)
  const [bucketLookup, setBucketLookup] = useState<Record<string, number>>({});

  // Resizable left/right split — % left pane width. Stored in a CSS variable
  // so dragging doesn't trigger React re-renders (critical with 13k+ rows).
  // The actual pct value is persisted to localStorage on mouseup.
  const STORAGE_KEY = 'drilldown_modal_left_pct';
  const initialPct = (() => {
    if (typeof window === 'undefined') return 60;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    const n = stored ? Number(stored) : NaN;
    return isFinite(n) && n >= 30 && n <= 85 ? n : 60;
  })();
  const splitContainerRef = useRef<HTMLDivElement>(null);
  const leftPaneRef = useRef<HTMLDivElement>(null);
  const rightPaneRef = useRef<HTMLDivElement>(null);
  const currentPctRef = useRef<number>(initialPct);
  const draggingRef = useRef(false);
  const rafRef = useRef<number | null>(null);

  const applyPctToDom = (pct: number) => {
    // Set CSS variables on the split container; the panes use these via inline style
    if (splitContainerRef.current) {
      splitContainerRef.current.style.setProperty('--drilldown-left-pct', `${pct}`);
      splitContainerRef.current.style.setProperty('--drilldown-right-pct', `${100 - pct}`);
    }
  };

  const onDragStart = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    draggingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!draggingRef.current || !splitContainerRef.current) return;
      const rect = splitContainerRef.current.getBoundingClientRect();
      const pct = ((e.clientX - rect.left) / rect.width) * 100;
      const clamped = Math.max(30, Math.min(85, pct));
      currentPctRef.current = clamped;
      // Throttle DOM updates to one per animation frame
      if (rafRef.current == null) {
        rafRef.current = requestAnimationFrame(() => {
          applyPctToDom(currentPctRef.current);
          rafRef.current = null;
        });
      }
    };
    const onUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.localStorage.setItem(STORAGE_KEY, String(currentPctRef.current));
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Apply the initial split width once the panes mount / when modal opens
  useEffect(() => {
    if (isOpen) {
      // Defer to next paint so refs are populated
      const id = requestAnimationFrame(() => applyPctToDom(currentPctRef.current));
      return () => cancelAnimationFrame(id);
    }
  }, [isOpen]);

  // Virtualizer ref — only render rows currently in view (critical for 13k+ rows)
  const tableScrollRef = useRef<HTMLDivElement>(null);
  // guards the async stats enrichment against hospital/type switches mid-flight
  const enrichReqRef = useRef<string>('');

  // ── Swipe-down-to-dismiss (mobile sheet) ───────────────────────────────────
  // Drag the grab-handle/header area down to close, like a native iOS sheet.
  // Attached only to the non-scrolling header region so it never fights the list.
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef<number | null>(null);
  const dragging = useRef(false);

  const onSheetTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    dragStartY.current = e.touches[0].clientY;
    dragging.current = true;
    if (sheetRef.current) sheetRef.current.style.transition = 'none';
  };
  const onSheetTouchMove = (e: React.TouchEvent) => {
    if (!dragging.current || dragStartY.current == null || !sheetRef.current) return;
    const dy = e.touches[0].clientY - dragStartY.current;
    sheetRef.current.style.transform = dy > 0 ? `translateY(${dy}px)` : '';
  };
  const onSheetTouchEnd = (e: React.TouchEvent) => {
    if (!dragging.current || dragStartY.current == null) return;
    const dy = (e.changedTouches[0]?.clientY ?? dragStartY.current) - dragStartY.current;
    dragging.current = false;
    dragStartY.current = null;
    const el = sheetRef.current;
    if (!el) return;
    if (dy > 110) {
      // Slide the rest of the way out, then close.
      hapticLight();
      el.style.transition = 'transform 0.2s ease-in';
      el.style.transform = 'translateY(100%)';
      window.setTimeout(onClose, 180);
    } else {
      el.style.transition = 'transform 0.25s ease-out';
      el.style.transform = '';
    }
  };

  // Reset any leftover drag transform when the sheet (re)opens + a soft tap.
  useEffect(() => {
    if (isOpen) {
      if (sheetRef.current) {
        sheetRef.current.style.transition = '';
        sheetRef.current.style.transform = '';
      }
      if (isMobile) hapticMedium();
    }
  }, [isOpen, isMobile]);

  // Scenario count for this hospital — populates header link badge. Must match
  // what the profile page actually ranks (cornerstone_pass + coverage >= 50 +
  // price > 0 + active scenarios), or the badge advertises a different number
  // than the page it links to. Prefer the static per-hospital shard (already
  // generated with exactly that filter set); fall back to a DB count with the
  // same filters.
  const [scenarioCount, setScenarioCount] = useState<number | null>(null);
  useEffect(() => {
    if (!isOpen || !hospitalId) { setScenarioCount(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const shard = await loadStaticData<{ n?: number; s?: unknown[] }>(
          `hospital_scenarios/${hospitalId}.json`
        );
        const shardCount = typeof shard?.n === 'number'
          ? shard.n
          : Array.isArray(shard?.s) ? shard!.s!.length : null;
        if (cancelled) return;
        if (shardCount != null) { setScenarioCount(shardCount); return; }
      } catch {
        // Shard unavailable — fall through to DB count
      }
      try {
        const { count, error } = await supabase
          .from('scenario_hospital_coverage')
          .select('scenario_id, scenarios!inner(is_active)', { count: 'exact', head: true })
          .eq('hospital_id', hospitalId)
          .eq('cornerstone_pass', true)
          .gte('coverage_percentage', 50)
          .gt('total_hospital_price', 0)
          .eq('scenarios.is_active', true);
        if (!cancelled && !error && typeof count === 'number') setScenarioCount(count);
      } catch {
        // Silently skip — link still works without count
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, hospitalId]);

  // Check if any procedures have NDC codes
  const hasNdcCodes = procedures.some(proc => proc.ndc_code && proc.ndc_code.trim() !== '');
  // Consumer names are AI-generated from hospitals' published descriptions —
  // whenever any are on screen the list carries the not-official-descriptor note.
  const aiNamesNote = useMemo(
    () => procedures.some(p => !!p.consumer_name)
      ? t('homepage:homeBeta.procedureDrilldown.aiNamesNote', { defaultValue: "Procedure names are AI-generated consumer-friendly names derived from hospitals' own published chargemaster descriptions — not official code descriptors. CPT® is a registered trademark of the AMA; not affiliated. Hover a row for the hospital's own wording." })
      : '',
    [procedures, t]
  );

  useEffect(() => {
    if (isOpen && hospitalId && procedureType) {
      fetchProcedures();
    }
  }, [isOpen, hospitalId, procedureType]);

  // Load global bucket-count lookup for this code type — same data the /cpt|/rev/<code>/ pages use
  useEffect(() => {
    if (!isOpen || !procedureType) return;
    let cancelled = false;
    (async () => {
      try {
        // Wire URL slug consistency: 'revenue' code type lives under codes_buckets_rev.json
        const slug = CODE_TYPE_TO_URL[procedureType] || procedureType;
        const wrapper = await loadStaticData<{ data: Record<string, number> }>(
          `codes_buckets_${slug}.json`
        );
        if (!cancelled && wrapper && wrapper.data) {
          setBucketLookup(wrapper.data);
        }
      } catch {
        // Lookup not available yet for this code type — silently fall back to local row count
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, procedureType]);

  // Auto-select the very first row (top of table by markup) when procedures load
  useEffect(() => {
    if (!procedures.length) {
      setSelectedCode(null);
      setSelectedProc(null);
      return;
    }
    setSelectedCode(procedures[0].procedure_code);
    setSelectedProc(procedures[0]);
  }, [procedures]);

  const fetchProcedures = async () => {
    if (!hospitalId || !procedureType) return;

    setLoading(true);
    setLoadError(false);
    try {
      const staticProcs = await tryStaticData(hospitalId, procedureType);
      if (staticProcs && staticProcs.length > 0) {
        console.log(`[Drilldown] Static data: ${staticProcs.length} ${procedureType} procedures`);
        // Rows render immediately; the median/percentile join fills in behind
        // them. Blocking first paint on this enrichment is what made the modal
        // feel slow — never await it here.
        setProcedures(staticProcs);
        const reqKey = `${hospitalId}:${procedureType}`;
        enrichReqRef.current = reqKey;
        void enrichNationalStats(staticProcs, procedureType).then(rows => {
          if (enrichReqRef.current === reqKey) setProcedures(rows);
        });
        return;
      }

      // In the native app the data model is static-only (the CDN shard/monolith).
      // The Supabase fallback can't reach an indexed result on a phone and would
      // hang indefinitely (the 838M-row analytics table), so surface a clean
      // retry state instead of an infinite spinner.
      if (isNativeApp()) {
        setProcedures([]);
        setLoadError(true);
        return;
      }

      console.log(`[Drilldown] Falling back to Supabase for ${procedureType}`);
      const { data: rankedData, error: rankedError } = await supabase
        .from('hospital_procedure_analytics')
        .select('procedure_code, procedure_description, hospital_gross_charge, national_avg_gross, gross_markup_percentage, duplicate_count, inpatient_outpatient, normalized_unit, ndc_code, hospital_min_negotiated, hospital_max_negotiated')
        .eq('hospital_id', hospitalId)
        .eq('procedure_type', procedureType)
        .not('gross_markup_percentage', 'is', null)
        .gt('national_avg_gross', 0)
        .order('gross_markup_percentage', { ascending: false });

      if (rankedError) throw rankedError;

      // National median / band / hospital count come from the same static
      // lookup as the shard path — a CDN file on every path, never a DB join.
      // Rows render first; the enrichment fills in behind them.
      const fallbackRows = (rankedData || []) as ProcedureDetail[];
      setProcedures(fallbackRows);
      const reqKey = `${hospitalId}:${procedureType}`;
      enrichReqRef.current = reqKey;
      void enrichNationalStats(fallbackRows, procedureType).then(rows => {
        if (enrichReqRef.current === reqKey) setProcedures(rows);
      });
    } catch (error) {
      console.error('Error fetching procedure details:', error);
      setProcedures([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  /** Try loading from static per-hospital JSON.
   *  Prefers the small per-code-type shard (hospitals/<id>/<ptype>.json — ~25KB-1.6MB)
   *  so the phone only downloads the code type being viewed. Falls back to the
   *  legacy monolith (hospitals/<id>.json — ~7.5MB, all code types + mappings)
   *  for hospitals not yet sharded. */
  async function tryStaticData(hid: string, ptype: string): Promise<ProcedureDetail[] | null> {
    type Raw = {
      c: string; d: string; hc: number; na: number; mp: number;
      io: string; u: string; ndc?: string; mn?: number; mx?: number; th?: number;
    };
    const mapRows = (rows: Raw[]): ProcedureDetail[] => rows.map(r => ({
      procedure_code: r.c,
      procedure_description: r.d,
      hospital_gross_charge: r.hc,
      national_avg_gross: r.na,
      gross_markup_percentage: r.mp,
      duplicate_count: 0,
      inpatient_outpatient: r.io,
      normalized_unit: r.u,
      ndc_code: r.ndc || '',
      total_hospitals: r.th,
      hospital_min_negotiated: r.mn,
      hospital_max_negotiated: r.mx,
    }));

    // 1) Small per-code-type shard
    try {
      const shard = await loadStaticData<Raw[]>(`hospitals/${hid}/${ptype}.json`);
      if (Array.isArray(shard) && shard.length > 0) return mapRows(shard);
    } catch { /* fall through to monolith */ }

    // 2) Legacy monolith fallback
    try {
      const data = await loadStaticData<Record<string, Raw[]>>(`hospitals/${hid}.json`);

      if (!data) return null;

      const rows = data[ptype];
      if (!rows || rows.length === 0) return null;
      return mapRows(rows);
    } catch {
      return null;
    }
  }

  const formatCurrency = (value: number | null | undefined) => {
    const numValue = value != null ? Number(value) : 0;
    if (!numValue || isNaN(numValue)) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numValue);
  };

  const formatPercent = (value: number | null | undefined) => {
    if (value == null) return '-';
    const numValue = Number(value);
    if (isNaN(numValue)) return '-';
    const absValue = Math.abs(numValue);
    const sign = numValue >= 0 ? '+' : '';
    if (absValue >= 1000) {
      return `${sign}${(numValue / 1000).toFixed(1)}k%`;
    }
    return `${sign}${numValue.toFixed(1)}%`;
  };

  const getCodeTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      cpt: 'CPT',
      hcpcs: 'HCPCS',
      drg: 'MS-DRG',
      ndc: 'NDC (Drugs)',
      revenue: 'Revenue',
      supply: 'Supply',
      drug: 'Drug',
    };
    return labels[type] || type.toUpperCase();
  };

  // iframe URL for the right pane on desktop. Includes optional hash anchor (e.g. #variations).
  // Query params let the embedded code page place THIS hospital on its price
  // band (see PriceRangeBand's `hospital` prop): hid = hospital, hp = the
  // selected row's listed (sticker) price, hn = display name.
  const iframeSrc = useMemo(() => {
    if (!selectedCode || !procedureType) return '';
    const q = new URLSearchParams({ embed: '1' });
    if (hospitalId) q.set('hid', hospitalId);
    const hp = selectedProc && selectedProc.procedure_code === selectedCode ? Number(selectedProc.hospital_gross_charge) : NaN;
    if (Number.isFinite(hp) && hp > 0) q.set('hp', String(Math.round(hp)));
    if (hospitalName) q.set('hn', hospitalName);
    return `${codePagePath(procedureType, selectedCode)}?${q.toString()}${iframeAnchor}`;
  }, [selectedCode, selectedProc, procedureType, iframeAnchor, hospitalId, hospitalName]);

  // External link (opens in a full new tab — for the per-row View button)
  const externalLink = (code: string) => codePagePath(procedureType, code);

  // Mobile drill-down: tapping a row navigates in-app to the full code page
  // (the desktop iframe preview pane doesn't exist on phones, so a tap that did
  // nothing felt broken). Close the sheet first so the back gesture returns here.
  const openCodePage = useCallback((code: string) => {
    const path = codePagePath(procedureType, code);
    if (!path) return;
    hapticLight();
    onClose();
    navigate(path);
  }, [procedureType, onClose, navigate]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      {/* grid-cols-[minmax(0,1fr)]: DialogContent is a CSS grid whose lone auto
          column otherwise sizes to the split pane's min-content (the 1,220px
          table + iframe ≈ 1,490px) and silently overflows a 1,296px dialog —
          clipping the iframe's right edge and anything at the far right of the
          header (the report card). Bounding the column keeps the header at the
          dialog's real width; the table still scrolls inside its own pane. */}
      <DialogContent
        ref={sheetRef}
        className="w-[95vw] sm:max-w-[90vw] xl:max-w-[90vw] max-h-[90vh] overflow-hidden p-4 sm:p-6
          grid-cols-[minmax(0,1fr)]
          z-[10050]
          max-md:left-0 max-md:right-0 max-md:bottom-0 max-md:top-auto
          max-md:translate-x-0 max-md:translate-y-0
          max-md:w-screen max-md:h-[92dvh] max-md:max-w-none max-md:max-h-none
          max-md:rounded-t-2xl max-md:rounded-b-none max-md:border-0 max-md:shadow-2xl
          max-md:flex max-md:flex-col max-md:p-0 max-md:gap-0
          max-md:data-[state=open]:slide-in-from-bottom max-md:data-[state=closed]:slide-out-to-bottom
          max-md:duration-300 max-md:ease-out
          max-md:[&>button.absolute]:top-3"
        style={{ paddingBottom: isMobile ? 'env(safe-area-inset-bottom)' : undefined }}
        aria-describedby="drilldown-desc"
      >
        {/* Drag region: grab handle + header. Swipe down here to dismiss. */}
        <div
          className="flex-shrink-0 max-md:touch-pan-y"
          onTouchStart={onSheetTouchStart}
          onTouchMove={onSheetTouchMove}
          onTouchEnd={onSheetTouchEnd}
        >
          {/* iOS-style grab handle (mobile only) */}
          <div className="md:hidden mx-auto mt-2.5 mb-1 h-1.5 w-9 rounded-full bg-muted-foreground/25" aria-hidden />
        <DialogHeader className="max-md:px-4 max-md:pt-1 max-md:space-y-1.5 max-md:text-left">
          <div className="flex items-start justify-between gap-4 pr-7">
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-base sm:text-xl leading-tight">
                {getCodeTypeLabel(procedureType || '')} Procedure Breakdown
                <span className="block text-sm font-normal text-muted-foreground truncate">{hospitalName}</span>
              </DialogTitle>
              <DialogDescription id="drilldown-desc" className="sr-only">
                Top procedures by markup percentage for {hospitalName}
              </DialogDescription>
            </div>
            {/* Far right: the hospital's report card — its overall A–F on our
                markup scale plus third-party quality (CMS ★, CMS survey, Google),
                so the breakdown reads like the ranking it came from. */}
            {!isMobile && quality && <HospitalQualityStrip quality={quality} />}
          </div>
          <div className="space-y-1">
            {isMobile ? (
              // Native chip row — big tap targets, in-app navigation (no external
              // browser pop on Capacitor), springy press feedback.
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className="inline-flex items-center gap-1.5 rounded-full bg-orange-500/10 px-3 py-1 text-xs font-semibold text-orange-600 dark:text-orange-400"
                  title={t('homepage:homeBeta.procedureDrilldown.medianMarkupTitle', { defaultValue: 'Median markup across the same codes vs national medians — the site\'s headline metric (it drives the A–F grade)' })}
                >
                  {t('homepage:homeBeta.procedureDrilldown.medianMarkupLabel', { defaultValue: 'Median Markup:' }).replace(':', '')}
                  <span className="font-bold tabular-nums">{medianMarkup != null ? formatPercent(medianMarkup) : '—'}</span>
                </span>
                <span
                  className="inline-flex items-center gap-1.5 rounded-full bg-orange-500/10 px-3 py-1 text-xs font-semibold text-orange-600 dark:text-orange-400"
                  title={t('homepage:homeBeta.procedureDrilldown.avgMarkupTitle', { defaultValue: 'Mean markup across this hospital\'s ranked codes of this type — a handful of extreme markups pull it well above the median' })}
                >
                  {t('homepage:homeBeta.procedureDrilldown.avgMarkupLabel', { defaultValue: 'Average Markup:' }).replace(':', '')}
                  <span className="font-bold tabular-nums">{formatPercent(avgMarkup)}</span>
                </span>
                {hospitalId && (
                  <button
                    type="button"
                    onClick={() => { onClose(); navigate(`/hospital/${hospitalId}#services`); }}
                    className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground active:scale-95 transition-transform"
                  >
                    <Building2 className="w-3.5 h-3.5" /> Hospital
                  </button>
                )}
                {hospitalId && procedureType && shareUrl && (
                  <button
                    type="button"
                    onClick={() => setShareModalOpen(true)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground active:scale-95 transition-transform"
                  >
                    <Share2 className="w-3.5 h-3.5" /> Share
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { onClose(); navigate('/data-methodology'); }}
                  className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground active:scale-95 transition-transform"
                >
                  <Info className="w-3.5 h-3.5" /> Method
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                {/* Median and average side by side, same weight: median is the
                    headline metric (drives A–F); the gap between them is the story. */}
                <span
                  className="cursor-help"
                  title={t('homepage:homeBeta.procedureDrilldown.medianMarkupTitle', { defaultValue: 'Median markup across the same codes vs national medians — the site\'s headline metric (it drives the A–F grade)' })}
                >
                  {t('homepage:homeBeta.procedureDrilldown.medianMarkupLabel', { defaultValue: 'Median Markup:' })} <span className="font-bold text-orange-500">{medianMarkup != null ? formatPercent(medianMarkup) : '—'}</span>
                </span>
                <span
                  className="cursor-help"
                  title={t('homepage:homeBeta.procedureDrilldown.avgMarkupTitle', { defaultValue: 'Mean markup across this hospital\'s ranked codes of this type — a handful of extreme markups pull it well above the median' })}
                >
                  {t('homepage:homeBeta.procedureDrilldown.avgMarkupLabel', { defaultValue: 'Average Markup:' })} <span className="font-bold text-orange-500">{formatPercent(avgMarkup)}</span>
                </span>
                <a
                  href="/data-methodology"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs underline hover:text-foreground"
                >
                  {t('homepage:homeBeta.procedureDrilldown.howCalculated')}
                </a>
                {hospitalId && (
                  <a
                    href={`/hospital/${hospitalId}#services`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs underline hover:text-foreground"
                    title={t('homepage:homeBeta.procedureDrilldown.viewHospitalProfileTitle')}
                  >
                    {t('homepage:homeBeta.procedureDrilldown.viewHospitalProfile')}
                  </a>
                )}
                {hospitalId && scenarioCount != null && scenarioCount > 0 && (
                  <a
                    href={`/hospital/${hospitalId}#scenarios`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs underline hover:text-foreground"
                    title={t('homepage:homeBeta.procedureDrilldown.scenariosRankedTitle')}
                  >
                    {t('homepage:homeBeta.procedureDrilldown.scenariosRanked', { count: scenarioCount.toLocaleString() })}
                  </a>
                )}
                {hospitalId && procedureType && shareUrl && (
                  <button
                    type="button"
                    onClick={() => setShareModalOpen(true)}
                    className="inline-flex items-center gap-1 text-xs underline hover:text-foreground"
                    title={t('homepage:homeBeta.procedureDrilldown.shareBreakdownTitle')}
                  >
                    <Share2 className="w-3 h-3" /> {t('homepage:homeBeta.procedureDrilldown.shareBreakdown')}
                  </button>
                )}
              </div>
            )}
            {procedureType === 'drg' && (
              <p className="text-xs text-muted-foreground italic max-md:px-0">
                {t('homepage:homeBeta.procedureDrilldown.drgNote')}
              </p>
            )}
            {isMobile && quality && (
              <HospitalQualityStrip
                quality={quality}
                compact
                onOpenMethodology={() => { onClose(); navigate(METHODOLOGY_GRADES_PATH); }}
              />
            )}
          </div>
        </DialogHeader>
        </div>

        {loading ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">
              Loading {getCodeTypeLabel(procedureType || '')} prices…
            </p>
          </div>
        ) : loadError || procedures.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-12 px-6 text-center">
            <p className="text-sm font-medium">
              {loadError ? "Couldn't load this breakdown" : 'No ranked procedures found'}
            </p>
            <p className="text-xs text-muted-foreground max-w-xs">
              {loadError
                ? 'Check your connection and try again.'
                : `This hospital has no ranked ${getCodeTypeLabel(procedureType || '')} procedures.`}
            </p>
            {loadError && (
              <button
                type="button"
                onClick={() => fetchProcedures()}
                className="mt-1 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground active:scale-95 transition-transform"
              >
                <Loader2 className="w-4 h-4" /> Try again
              </button>
            )}
          </div>
        ) : isMobile ? (
          <ProcedureMobileList
            procedures={procedures}
            procedureType={procedureType}
            bucketLookup={bucketLookup}
            formatCurrency={formatCurrency}
            formatPercent={formatPercent}
            onRowTap={openCodePage}
            aiNamesNote={aiNamesNote}
          />
        ) : (
          <div
            ref={splitContainerRef}
            className="mt-4 flex flex-1 min-h-0 flex-col lg:flex-row gap-0 lg:h-[75vh] relative"
            style={{
              // Initial values; updated reactively via applyPctToDom on drag/open
              ['--drilldown-left-pct' as any]: String(initialPct),
              ['--drilldown-right-pct' as any]: String(100 - initialPct),
            }}
          >
            {/* Left: procedure table (virtualized for 13k+ rows). Hidden when iframe fullscreen. */}
            <div
              ref={leftPaneRef}
              className={`flex-1 lg:flex-none flex flex-col min-h-0 overflow-hidden w-full lg:w-[var(--drilldown-left-w,60%)] ${iframeFullscreen ? 'lg:hidden' : ''}`}
              style={{
                // Mobile: full width. Desktop: driven by --drilldown-left-pct unless fullscreen.
                width: typeof window !== 'undefined' && window.innerWidth >= 1024
                  ? (iframeFullscreen ? '0' : 'calc(var(--drilldown-left-pct) * 1% - 6px)')
                  : '100%',
              }}
            >
              <ProcedureVirtualTable
                tableScrollRef={tableScrollRef}
                procedures={procedures}
                hasNdcCodes={hasNdcCodes}
                selectedCode={selectedCode}
                setSelectedCode={(code, proc) => { setSelectedCode(code); setSelectedProc(proc ?? procedures.find(p => p.procedure_code === code) ?? null); setIframeAnchor(''); }}
                onShowVariations={(code) => { setSelectedCode(code); setSelectedProc(procedures.find(p => p.procedure_code === code) ?? null); setIframeAnchor('#variations'); }}
                procedureType={procedureType}
                formatCurrency={formatCurrency}
                formatPercent={formatPercent}
                externalLink={externalLink}
                bucketLookup={bucketLookup}
                aiNamesNote={aiNamesNote}
              />
            </div>

            {/* Drag handle (desktop only) */}
            <div
              onMouseDown={onDragStart}
              className={`hidden ${iframeFullscreen ? '' : 'lg:flex'} w-3 cursor-col-resize items-center justify-center group flex-shrink-0`}
              title={t('homepage:homeBeta.procedureDrilldown.dragResize')}
              role="separator"
              aria-orientation="vertical"
            >
              <div className="h-12 w-[3px] rounded bg-border group-hover:bg-primary/60 group-active:bg-primary transition-colors" />
            </div>

            {/* Right: iframe preview pane (desktop only).
                When iframeFullscreen=true the pane fills the modal width and the left table is hidden. */}
            <div
              ref={rightPaneRef}
              className="hidden lg:flex flex-col border rounded-md overflow-hidden bg-background flex-none min-h-0"
              style={{
                width: iframeFullscreen
                  ? '100%'
                  : 'calc(var(--drilldown-right-pct) * 1% - 6px)',
              }}
            >
              <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/40 text-xs gap-2">
                <span className="font-medium truncate">
                  {selectedCode ? (
                    <>
                      <span className="opacity-60">{getCodeTypeLabel(procedureType || '')}: </span>
                      <span className="font-mono">{selectedCode}</span>
                    </>
                  ) : (
                    'Select a row to preview'
                  )}
                </span>
                {selectedCode && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setIframeFullscreen(v => !v)}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-border hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      title={iframeFullscreen ? 'Restore split view' : 'Expand to full width (hide table)'}
                    >
                      {iframeFullscreen ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
                      <span>{iframeFullscreen ? 'Restore' : 'Fullscreen'}</span>
                    </button>
                    <a
                      href={externalLink(selectedCode)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-border hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      title={t('homepage:homeBeta.procedureDrilldown.openPageTitle')}
                    >
                      <ExternalLink className="w-3 h-3" />
                      <span>{t('homepage:homeBeta.procedureDrilldown.openPage')}</span>
                    </a>
                  </div>
                )}
              </div>
              {iframeSrc ? (
                <iframe
                  key={iframeSrc}
                  src={iframeSrc}
                  title={`${selectedCode} breakdown`}
                  className="w-full flex-1 border-0"
                  sandbox="allow-scripts allow-same-origin allow-popups"
                />
              ) : (
                <div className="flex-1 flex items-center justify-center text-xs text-muted-foreground p-4">
                  Click any row to load the full code-level breakdown here.
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>

      {/* "Spread the Word" sharing modal — Dignity + scope picker + multi-platform launch */}
      <ShareDignityModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        shareUrl={shareUrl}
        hospitalId={hospitalId || undefined}
        hospitalName={hospitalName}
        codeType={procedureType || undefined}
        code={selectedCode || undefined}
        codeTypeLabel={getCodeTypeLabel(procedureType || '')}
        markupPercentage={avgMarkup}
      />
    </Dialog>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// ProcedureVirtualTable — virtualized rendering for the procedure list.
// Only renders ~30 visible rows + small overscan instead of all 13k.
// ────────────────────────────────────────────────────────────────────────────
interface ProcedureVirtualTableProps {
  tableScrollRef: React.RefObject<HTMLDivElement>;
  procedures: ProcedureDetail[];
  hasNdcCodes: boolean;
  selectedCode: string | null;
  setSelectedCode: (code: string, proc?: ProcedureDetail) => void;
  onShowVariations: (code: string) => void;
  procedureType: string | null;
  formatCurrency: (value: number | null | undefined) => string;
  formatPercent: (value: number | null | undefined) => string;
  externalLink: (code: string) => string;
  bucketLookup: Record<string, number>;
  aiNamesNote: string;
}

function ProcedureVirtualTable({
  tableScrollRef,
  procedures,
  hasNdcCodes,
  selectedCode,
  setSelectedCode,
  onShowVariations,
  procedureType,
  formatCurrency,
  formatPercent,
  externalLink,
  bucketLookup,
  aiNamesNote,
}: ProcedureVirtualTableProps) {
  const rowVirtualizer = useVirtualizer({
    count: procedures.length,
    getScrollElement: () => tableScrollRef.current,
    estimateSize: () => 44,  // 2-line Compare button needs more height
    overscan: 12,
  });

  // Local count = rows sharing this procedure_code in this hospital's data.
  // Used as a fallback when the global bucket lookup hasn't loaded for this code type yet.
  const localCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of procedures) {
      counts[p.procedure_code] = (counts[p.procedure_code] || 0) + 1;
    }
    return counts;
  }, [procedures]);

  // Prefer the GLOBAL bucket count (from codes_buckets_<type>.json — the same data
  // source that powers /cpt|/rev|/hcpcs/<code>/). Fall back to per-hospital row count.
  const variationCount = (code: string): number =>
    bucketLookup[code] ?? localCounts[code] ?? 1;

  // The column-label row sits outside the body's scroll container (so it can
  // stay put vertically), which means it does not scroll horizontally with the
  // rows. Mirror the body's scrollLeft onto it so labels stay over their columns.
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const onBodyScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (headerScrollRef.current) headerScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
  }, []);

  const colCount = hasNdcCodes ? 12 : 11;
  // Mobile: collapse to the 4 columns that matter (Code · Description · Charge ·
  // Markup%) so the table fits the phone instead of clipping 5 desktop columns.
  const isNarrow = typeof window !== 'undefined' && window.innerWidth < 1024;

  // Column widths defined as a ratio so they're consistent between header and rows
  const descTitle = "Consumer-friendly name when we have one (AI-generated from hospitals' published descriptions — not the official code descriptor). Hover a row for this hospital's own chargemaster wording.";
  const cols: { key: string; label: string; cls: string; title?: string }[] = isNarrow ? [
    { key: 'code', label: 'Code', cls: 'text-left' },
    { key: 'desc', label: 'Description', cls: 'text-left flex-1 min-w-0', title: descTitle },
    { key: 'charge', label: procedureType === 'drg' ? 'Price' : 'Charge', cls: 'text-right' },
    { key: 'markup', label: 'Markup', cls: 'text-right' },
  ] : [
    { key: 'code', label: 'Code', cls: 'text-left' },
    { key: 'desc', label: 'Description', cls: 'text-left flex-1 min-w-0', title: descTitle },
    ...(hasNdcCodes ? [{ key: 'ndc', label: 'NDC', cls: 'text-left' }] : []),
    { key: 'charge', label: procedureType === 'drg' ? 'Hospital Price Range' : 'Hospital Charge', cls: 'text-right' },
    { key: 'natmed', label: 'National Median', cls: 'text-right' },
    { key: 'natavg', label: 'National Avg', cls: 'text-right' },
    { key: 'natrange', label: '10%–90% Range', cls: 'text-right' },
    { key: 'hosp', label: 'Hospitals', cls: 'text-center' },
    { key: 'markup', label: procedureType === 'drg' ? 'Max Markup %' : 'Markup %', cls: 'text-right' },
    { key: 'setting', label: 'Setting', cls: 'text-center' },
    { key: 'unit', label: 'Unit', cls: 'text-center' },
    { key: 'view', label: 'Compare prices', cls: 'text-center' },
  ];

  // grid template defining column widths
  const gridTemplate = isNarrow
    ? '56px minmax(0,1fr) 76px 62px'
    : hasNdcCodes
      ? '70px minmax(180px, 1fr) 120px 110px 110px 110px 150px 80px 100px 90px 80px 140px'
      : '70px minmax(180px, 1fr) 110px 110px 110px 150px 80px 100px 90px 80px 140px';

  if (procedures.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        No ranked procedures found
      </div>
    );
  }

  return (
    <>
      {/* Column labels — clipped to the pane, horizontally synced to the rows */}
      <div ref={headerScrollRef} className="overflow-hidden flex-shrink-0">
        <div
          className="grid border-b bg-muted/50 text-sm font-semibold"
          style={{ gridTemplateColumns: gridTemplate }}
        >
          {cols.map(c => (
            <div key={c.key} className={`p-2 ${c.cls}${c.title ? ' cursor-help' : ''}`} title={c.title}>{c.label}</div>
          ))}
        </div>
      </div>

      {/* Virtualized rows */}
      <div ref={tableScrollRef} onScroll={onBodyScroll} className="flex-1 min-h-0 overflow-auto" style={{ contain: 'strict' }}>
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map(virtualRow => {
            const proc = procedures[virtualRow.index];
            const isSelected = proc.procedure_code === selectedCode;
            return (
              <div
                key={virtualRow.index}
                onClick={() => setSelectedCode(proc.procedure_code, proc)}
                className={`grid border-b cursor-pointer transition-colors absolute top-0 left-0 right-0 ${
                  isSelected ? 'bg-primary/10 hover:bg-primary/15' : 'hover:bg-muted/30'
                }`}
                style={{
                  gridTemplateColumns: gridTemplate,
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <div className="p-2 font-mono text-xs">{proc.procedure_code}</div>
                {/* Consumer name leads; the hospital's raw chargemaster wording
                    stays in the tooltip — it's what you match against a bill. */}
                <div
                  className="p-2 text-xs truncate self-center"
                  title={proc.consumer_name
                    ? `Hospital's chargemaster wording: ${proc.procedure_description}`
                    : proc.procedure_description}
                >
                  {proc.consumer_name || proc.procedure_description}
                </div>
                {hasNdcCodes && !isNarrow && (
                  <div className="p-2 font-mono text-xs text-muted-foreground self-center">
                    {proc.ndc_code || '—'}
                  </div>
                )}
                <div className="p-2 text-right font-mono text-xs self-center">
                  {procedureType === 'drg' && proc.hospital_min_negotiated && proc.hospital_max_negotiated ? (
                    <span className="text-orange-500" title={`${formatCurrency(proc.hospital_min_negotiated)} - ${formatCurrency(proc.hospital_max_negotiated)}`}>
                      {formatCurrency(proc.hospital_max_negotiated)}
                    </span>
                  ) : (
                    formatCurrency(proc.hospital_gross_charge)
                  )}
                </div>
                {!isNarrow && (
                  <div className="p-2 text-right font-mono text-xs self-center">
                    {proc.national_median_gross != null ? formatCurrency(proc.national_median_gross) : '—'}
                  </div>
                )}
                {!isNarrow && (
                  <div className="p-2 text-right font-mono text-xs self-center">
                    {formatCurrency(proc.national_avg_gross)}
                  </div>
                )}
                {!isNarrow && (
                  <div className="p-2 text-right font-mono text-[11px] self-center whitespace-nowrap">
                    {proc.national_p10_gross != null && proc.national_p90_gross != null ? (
                      <>
                        <span className="text-emerald-600">{formatCurrency(proc.national_p10_gross)}</span>
                        <span className="text-muted-foreground"> – </span>
                        <span className="text-red-600">{formatCurrency(proc.national_p90_gross)}</span>
                      </>
                    ) : '—'}
                  </div>
                )}
                {!isNarrow && (
                  <div className="p-2 text-center font-mono text-xs text-muted-foreground self-center">
                    {proc.total_hospitals ? proc.total_hospitals.toLocaleString() : '—'}
                  </div>
                )}
                <div className="p-2 text-right font-mono text-xs font-bold text-orange-500 self-center">
                  {formatPercent(proc.gross_markup_percentage)}
                </div>
                {!isNarrow && (
                  <div className="p-2 text-center text-xs self-center">
                    {proc.inpatient_outpatient || '-'}
                  </div>
                )}
                {!isNarrow && (
                  <div className="p-2 text-center text-xs font-mono self-center">
                    {proc.normalized_unit || '-'}
                  </div>
                )}
                {!isNarrow && (
                <div className="p-2 self-center min-w-0 overflow-hidden" onClick={e => e.stopPropagation()}>
                  {(() => {
                    const n = variationCount(proc.procedure_code);
                    return (
                      <button
                        type="button"
                        onClick={() => onShowVariations(proc.procedure_code)}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-semibold bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 hover:border-primary/60 transition-colors w-full max-w-full"
                        title={`See ${n} different price${n === 1 ? '' : 's'} for this code — varies by setting (inpatient/outpatient/both), unit (each, mg, mL…), and dosage`}
                      >
                        <Layers className="w-3.5 h-3.5 flex-shrink-0" />
                        <div className="flex flex-col items-start leading-tight min-w-0">
                          <span className="truncate">{n === 1 ? 'Compare' : `Compare ${n}`}</span>
                          <span className="text-[9px] font-normal opacity-75 truncate">
                            {n === 1 ? 'no variation' : 'in/out × unit × dose'}
                          </span>
                        </div>
                      </button>
                    );
                  })()}
                </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-2 text-center px-2 pb-2 flex-shrink-0">
        Showing {procedures.length.toLocaleString()} procedures sorted by markup
        <span className="hidden lg:inline"> · click a row to preview · drag the divider to resize</span>
      </p>
      {aiNamesNote && (
        <p className="text-[10px] leading-snug text-muted-foreground/70 text-center px-3 pb-2 -mt-1 flex-shrink-0">
          {aiNamesNote}
        </p>
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// ProcedureMobileList — native, fluid list for phones (replaces the spreadsheet
// grid). Virtualized for 11k+ rows, momentum scroll, big tap targets with press
// feedback, graded markup pills, and a tap = drill into the full code page.
// ────────────────────────────────────────────────────────────────────────────
interface ProcedureMobileListProps {
  procedures: ProcedureDetail[];
  procedureType: string | null;
  bucketLookup: Record<string, number>;
  formatCurrency: (value: number | null | undefined) => string;
  formatPercent: (value: number | null | undefined) => string;
  onRowTap: (code: string) => void;
  aiNamesNote: string;
}

// Severity-graded pill so the eye lands on the worst offenders instantly.
function markupPillClass(mp: number | null | undefined): string {
  if (mp == null) return 'bg-muted text-muted-foreground';
  if (mp >= 100) return 'bg-red-500/15 text-red-600 dark:text-red-400';
  if (mp >= 10) return 'bg-orange-500/15 text-orange-600 dark:text-orange-400';
  if (mp > -10) return 'bg-muted text-muted-foreground';
  return 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400';
}

function ProcedureMobileList({
  procedures,
  procedureType,
  bucketLookup,
  formatCurrency,
  formatPercent,
  onRowTap,
  aiNamesNote,
}: ProcedureMobileListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: procedures.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 76,
    overscan: 8,
  });

  const localCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of procedures) counts[p.procedure_code] = (counts[p.procedure_code] || 0) + 1;
    return counts;
  }, [procedures]);
  const variationCount = (code: string): number => bucketLookup[code] ?? localCounts[code] ?? 1;

  return (
    <div className="flex flex-1 min-h-0 flex-col">
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
        style={{ contain: 'strict', WebkitOverflowScrolling: 'touch' }}
      >
        <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, width: '100%', position: 'relative' }}>
          {rowVirtualizer.getVirtualItems().map(virtualRow => {
            const proc = procedures[virtualRow.index];
            const charge = procedureType === 'drg' && proc.hospital_max_negotiated
              ? proc.hospital_max_negotiated
              : proc.hospital_gross_charge;
            const n = variationCount(proc.procedure_code);
            return (
              <button
                key={virtualRow.index}
                type="button"
                onClick={() => onRowTap(proc.procedure_code)}
                className="absolute left-0 right-0 top-0 flex w-full items-center gap-3 border-b border-border/60 px-4 text-left transition-colors active:bg-muted"
                style={{ height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)` }}
              >
                <div className="min-w-0 flex-1">
                  {/* No hover on a phone, so the hospital's own wording gets a
                      small line of its own under the consumer name. */}
                  <div className={`${proc.consumer_name ? 'line-clamp-1' : 'line-clamp-2'} text-[15px] font-medium leading-snug text-foreground`}>
                    {proc.consumer_name || proc.procedure_description || proc.procedure_code}
                  </div>
                  {proc.consumer_name && (
                    <div className="truncate text-[11px] italic text-muted-foreground/80">{proc.procedure_description}</div>
                  )}
                  <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="font-mono">{proc.procedure_code}</span>
                    {proc.inpatient_outpatient && proc.inpatient_outpatient !== 'unspecified' && (
                      <span className="opacity-60">· {proc.inpatient_outpatient}</span>
                    )}
                    {n > 1 && <span className="opacity-60">· {n} prices</span>}
                  </div>
                </div>
                <div className="flex flex-shrink-0 flex-col items-end gap-1">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold tabular-nums ${markupPillClass(proc.gross_markup_percentage)}`}>
                    {formatPercent(proc.gross_markup_percentage)}
                  </span>
                  <span className="text-xs text-muted-foreground tabular-nums">{formatCurrency(charge)}</span>
                </div>
                <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground/40" />
              </button>
            );
          })}
        </div>
      </div>
      <p className="flex-shrink-0 px-4 py-2 text-center text-[11px] text-muted-foreground">
        {procedures.length.toLocaleString()} procedures · sorted by markup · tap for detail
      </p>
      {aiNamesNote && (
        <p className="flex-shrink-0 px-4 pb-2 -mt-1 text-center text-[10px] leading-snug text-muted-foreground/70">
          {aiNamesNote}
        </p>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// HospitalQualityStrip — the hospital's report card, far right of the header.
// Overall A–F on OUR markup scale (same getHospitalGradeInfo the rankings/map
// use) next to THIRD-PARTY quality we merely relay: CMS Care Compare stars,
// the CMS HCAHPS "would recommend" share (its own past patients — never an AHP
// recommendation), and the public Google rating. Same wording as /cpt pages.
// ────────────────────────────────────────────────────────────────────────────
const CMS_STAR_GOLD = 'var(--civic-gold)';

const METHODOLOGY_GRADES_PATH = '/data-methodology#letter-grades';

function HospitalQualityStrip({ quality, compact = false, onOpenMethodology }: {
  quality: HospitalQualitySnapshot;
  compact?: boolean;
  /** Mobile/native: in-app navigation instead of a plain href (no external-browser pop on Capacitor) */
  onOpenMethodology?: () => void;
}) {
  const gi = getHospitalGradeInfo(quality.overallMedianMarkup);
  const grade: string = gi.grade;
  // The grade is a published computation on the hospital's own filed prices —
  // say so next to the letter, every time, so it can never read as an opinion.
  const gradeBasis = "Computed, not opinion: this hospital's own published prices vs. national medians for the same billing codes, on a fixed scale applied to every hospital.";
  const gradeTitle = grade === 'N/A'
    ? `Not yet graded — this hospital has no overall median markup on file. ${gradeBasis}`
    : `Overall grade ${grade} on our A–F scale: this hospital's median markup across all its ranked codes vs national medians${quality.overallMedianMarkup != null ? ` (${quality.overallMedianMarkup >= 0 ? '+' : ''}${Math.round(quality.overallMedianMarkup).toLocaleString()}%)` : ''}${quality.overallRank ? ` · ranked #${quality.overallRank.toLocaleString()} nationally` : ''}. ${gradeBasis} Same scale as the rankings and map.`;
  const cmsTitle = quality.cmsStars
    ? `CMS Care Compare overall rating — published by the Centers for Medicare & Medicaid Services, not us. ${[
        quality.cmsMortality && `Mortality: ${quality.cmsMortality}`,
        quality.cmsSafety && `Safety: ${quality.cmsSafety}`,
        quality.cmsReadmission && `Readmission: ${quality.cmsReadmission}`,
      ].filter(Boolean).join(' · ')}`
    : 'Not rated by CMS — Care Compare does not rate many small or specialty hospitals';
  const recommendTitle = 'HCAHPS, the federal patient survey run by CMS. Discharged patients are asked whether they would recommend this hospital to friends and family; this is the share of ITS OWN past patients who answered "definitely yes". A CMS survey result, not an American Hospital Prices recommendation.';
  const googleTitle = 'Public Google rating and review count as of our Feb–Mar 2026 snapshot — Google\'s users, not us.';
  const none = <span className="opacity-40">not rated</span>;

  const stars = quality.cmsStars
    ? <span style={{ color: CMS_STAR_GOLD, letterSpacing: '0.5px' }}>
        {'★'.repeat(quality.cmsStars)}<span className="opacity-25">{'★'.repeat(Math.max(0, 5 - quality.cmsStars))}</span>
      </span>
    : none;

  if (compact) {
    return (
      <div className="pt-1">
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar text-[11px] text-muted-foreground" aria-label="Hospital report card">
        <span
          className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-sm font-black text-white shadow-sm"
          style={{ background: gi.color }}
          title={gradeTitle}
        >{grade === 'N/A' ? '–' : grade}</span>
        <span className="flex-shrink-0 whitespace-nowrap" title={cmsTitle}>CMS {stars}</span>
        <span className="flex-shrink-0 whitespace-nowrap font-mono tabular-nums" title={recommendTitle}>
          {quality.recommendPct != null ? `${quality.recommendPct}%` : none} <span className="font-sans opacity-70">recommend</span>
        </span>
        <span className="flex-shrink-0 whitespace-nowrap font-mono tabular-nums" title={googleTitle}>
          {quality.googleRating != null ? <>{Number(quality.googleRating).toFixed(1)}<span className="opacity-50">★</span></> : none}
        </span>
      </div>
      <button
        type="button"
        onClick={onOpenMethodology}
        className="mt-1 block text-left text-[10px] leading-snug text-muted-foreground/80 active:text-foreground"
      >
        Grade is computed from the hospital's own published prices vs. national medians — not opinion.{' '}
        <span className="underline underline-offset-2">How we grade →</span>
      </button>
      </div>
    );
  }

  return (
    <div className="hidden md:flex flex-shrink-0 flex-col items-end gap-1">
    <div className="flex items-stretch gap-3 rounded-lg border bg-muted/30 px-3 py-2 text-xs" aria-label="Hospital report card">
      {/* Schoolhouse grade — the same letter the rankings table and map show */}
      <div className="flex flex-col items-center justify-center gap-0.5 cursor-help" title={gradeTitle}>
        <span
          className="inline-flex h-10 w-10 items-center justify-center rounded-md text-xl font-black text-white shadow"
          style={{ background: gi.color }}
        >{grade === 'N/A' ? '–' : grade}</span>
        <span className="text-[9px] uppercase tracking-wider text-muted-foreground">Overall grade</span>
      </div>
      <div className="w-px bg-border" />
      <div className="flex flex-col justify-center gap-0.5 cursor-help whitespace-nowrap" title={cmsTitle}>
        <span className="text-[9px] uppercase tracking-wider text-muted-foreground">CMS ★</span>
        <span className="text-sm leading-none">{stars}</span>
      </div>
      <div className="flex flex-col justify-center gap-0.5 cursor-help whitespace-nowrap" title={recommendTitle}>
        <span className="text-[9px] uppercase tracking-wider text-muted-foreground">Patients recommend</span>
        <span className="text-sm font-mono tabular-nums leading-none">{quality.recommendPct != null ? `${quality.recommendPct}%` : none}</span>
        <span className="text-[8px] leading-none text-muted-foreground/80">% of its past patients · CMS survey, not ours</span>
      </div>
      <div className="flex flex-col justify-center gap-0.5 cursor-help whitespace-nowrap" title={googleTitle}>
        <span className="text-[9px] uppercase tracking-wider text-muted-foreground">Google</span>
        <span className="text-sm font-mono tabular-nums leading-none whitespace-nowrap">
          {quality.googleRating != null
            ? <>{Number(quality.googleRating).toFixed(1)}<span className="opacity-50 ml-0.5">★</span>
                {quality.googleReviews != null && <span className="opacity-45 text-[10px] ml-1">({Number(quality.googleReviews).toLocaleString()})</span>}</>
            : none}
        </span>
      </div>
    </div>
      {/* Legal framing for the letter: a published computation on the hospital's
          own filed prices, on a scale applied to every hospital — never a judgment. */}
      <a
        href={METHODOLOGY_GRADES_PATH}
        target="_blank"
        rel="noopener noreferrer"
        className="max-w-[440px] text-right text-[9px] leading-snug text-muted-foreground/80 hover:text-foreground"
        title={gradeBasis}
      >
        Grade is computed, not opinion: this hospital's own published prices vs. national medians for the same billing codes.{' '}
        <span className="underline underline-offset-2 whitespace-nowrap">How we grade →</span>
      </a>
    </div>
  );
}
